// the purpose of local preferences is to abstract local storage for flags/settings/preferences
import '../interfaces/assertInterface';
import { getBuildConfig, isDevMode } from '../config';
import { localStorageInterface } from '../interfaces/localStorageInterface';

import keys from './keys';
export { keys };

export function getItem(key) {
  let value;
  let jsonValue;
  try {
    jsonValue = localStorageInterface?.getItem(key);
    if (jsonValue) {
      value = JSON.parse(jsonValue);
    }
  } catch (e) {
    assert(false, e, { key, jsonValue });
  }

  return value;
}

export function setItem(key, value) {
  if (value === undefined) {
    removeItem(key);
  } else {
    const jsonValue = JSON.stringify(value);
    try {
      localStorageInterface?.setItem(key, jsonValue);
    } catch (e) {
      assert(false, e, { key, value });
      if (e.code === 22 || e.name === 'NS_ERROR_FILE_CORRUPTED') {
        // QuotaExceededError / File error: Corrupted
        localStorageInterface?.clear();
      }
    }
  }
}

export function removeItem(key) {
  try {
    localStorageInterface?.removeItem(key);
  } catch (e) {
    assert(false, `can't delete local storage data for "${key}"\n${e}`);
  }
}

// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Getters and Setters for the Preferences
// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const getAuthSession = () => getItem(keys.authSession);
export const setAuthSession = (value) => setItem(keys.authSession, value);
export const removeAuthSession = () => removeItem(keys.authSession);

export const getDebug = () => getItem(keys.debug);
export const setDebug = (value) => setItem(keys.debug, value);
export const removeDebug = () => removeItem(keys.debug);

export const getPersistPurgeVersion = () => getItem(keys.persistPurgeVersion);
export const setPersistPurgeVersion = (value) => setItem(keys.persistPurgeVersion, value);
export const removePersistPurgeVersion = () => removeItem(keys.persistPurgeVersion);

export function getDisableBugsnag() {
  let value = false;
  if (isDevMode()) {
    value = getItem(keys.disableBugsnag) || false;
  }
  return value;
}
export const setDisableBugsnag = (value) => setItem(keys.disableBugsnag, value);

export const getEntitlements = () => getItem(keys.entitlements);
export const setEntitlements = (value) => setItem(keys.entitlements, value);
export const removeEntitlements = () => removeItem(keys.entitlements);

export const getFeatureFlagsFreeze = () => {
  let featureFlagsFreeze = false;
  if (isDevMode()) {
    featureFlagsFreeze = getItem(keys.featureFlagsFreeze);
  }
  return featureFlagsFreeze;
};
export const setFeatureFlagsFreeze = (value) => setItem(keys.featureFlagsFreeze, value);

export const setGetSessionStorage = (value) => setItem(keys.getSessionStorage, value);

export function getIgnoreBugsnagReport() {
  let value = false;
  if (isDevMode()) {
    value = getItem(keys.ignoreBugsnagReport) || Boolean(getBuildConfig().bugsnag_release_stage === 'development');
  }
  return value;
}
export const setIgnoreBugsnagReport = (value) => setItem(keys.ignoreBugsnagReport, value);

export const getKeepLoggedIn = () => getItem(keys.keepLoggedIn);
export const setKeepLoggedIn = (value) => setItem(keys.keepLoggedIn, value);

// TODO: LastSession

export const getMCPTestMode = () => {
  let mcpTestMode;
  if (isDevMode()) {
    mcpTestMode = getItem(keys.mcpTestMode);
  }
  return mcpTestMode;
};
export const setMCPTestMode = (value) => setItem(keys.mcpTestMode, value);

export const getMobileWarningShown = () => getItem(keys.mobileWarningShown);
export const setMobileWarningShown = (value) => setItem(keys.mobileWarningShown, value);

export const getOfflineSupport = () => getItem(keys.offlineSupport);
export const setOfflineSupport = (value) => setItem(keys.offlineSupport, value);

export const getPersist = () => {
  let value = getItem(keys.persist) ?? false;
  if (isDevMode()) {
    value = getPersistOverride() ?? value;
  }
  return value;
};
export const setPersist = (value) => setItem(keys.persist, value);

export const getPersistOverride = () => getItem(keys.persistOverride);
export const setPersistOverride = (value) => setItem(keys.persistOverride, value);

export const getPollDataset = () => getItem(keys.pollDataset);
export const setPollDataset = (value) => setItem(keys.pollDataset, value);
export const removePollDataset = () => removeItem(keys.pollDataset);

export const getSelectorsProfiling = () => {
  let value = false;
  if (isDevMode()) {
    value = getItem(keys.selectorsProfiling);
  }
  return value;
};
export const setSelectorsProfiling = (value) => setItem(keys.selectorsProfiling, value);

export const setSessionStorage = (value) => setItem(keys.sessionStorage, value);
export const removeSessionStorage = () => removeItem(keys.sessionStorage);

export function getSuppressAssertPopups() {
  let value = false;
  if (isDevMode()) {
    value = getItem(keys.suppressAssertPopups) || false;
  }
  return value;
}
export const setSuppressAssertPopups = (value) => setItem(keys.suppressAssertPopups, value);

export function getSuppressWindowError() {
  let value = true;
  if (isDevMode()) {
    value = getItem(keys.suppressWindowError) || false;
  }
  return value;
}
export const setSuppressWindowError = (value) => setItem(keys.suppressWindowError, value);

export const getWhyDidYouRender = () => {
  let whyDidYouRender = false;
  if (isDevMode()) {
    whyDidYouRender = getItem(keys.whyDidYouRender);
  }
  return whyDidYouRender;
};
export const setWhyDidYouRender = (value) => setItem(keys.whyDidYouRender, value);

export const getWDYRSettings = () => ({
  collapseGroups: true,
  logOnDifferentValues: false,
  logOwnerReasons: true,
  onlyLogs: false,
  trackAllPureComponents: true,
  trackHooks: true,
  virtualIncludeAll: false,
  ...getItem(keys.wdyrSettings),
});
export const setWDYRSettings = (value) => setItem(keys.wdyrSettings, value);

export const setLogoutReason = (value) => setItem(keys.logoutReason, value);
export const getLogoutReason = () => getItem(keys.logoutReason);
